/* ──────────────────────────────────────────────────────────────────────────
 * Kumbh Digital Twin — entity catalogue (§4).
 *
 * The brief is explicit that this is "not a 3D visual gimmick" but a real
 * operational twin, and it names the property that makes the difference:
 *
 *   Each has: location + capacity + current state + historical state +
 *             relationships + risk + responsible agency.
 *
 * "Relationships" is the load-bearing word. A list of assets with coordinates
 * is a map layer. What makes it a twin is that a barricade knows which
 * corridor it constrains, a corridor knows which ghat it feeds, and a ghat
 * knows which exits drain it — so the platform can answer "what does closing
 * this affect" rather than just "where is this".
 *
 * That graph is what §11 (mobility chain) and §12 (emergency routing)
 * traverse. Neither can be built honestly without it, which is why this
 * comes first.
 *
 * ── PROVENANCE ──
 * The real places (hospitals, the railway station, ghats) already live in
 * kumbhGeo.js and are imported rather than duplicated. Everything added here
 * — barricades, corridors, junctions, PA arrays, camera positions, officer
 * units — is invented for the demo and carries a DEMO provenance. A real
 * deployment replaces this file with the district asset register.
 * ────────────────────────────────────────────────────────────────────────── */

import {
  SECTORS, GHATS, ENTRY_GATES, EXIT_GATES, FACILITIES, ROUTES, CONGREGATION,
} from "../geo/data/kumbhGeo";
import { withProvenance, CLASSIFICATION, SOURCE_KIND, TRUST } from "../geo/data/provenance";

const demo = (note, classification = CLASSIFICATION.INTERNAL) => ({
  trust: TRUST.SIMULATED,
  sourceKind: SOURCE_KIND.SIMULATION,
  classification,
  note,
});

/* ── Responsible agencies ──────────────────────────────────────────────
 * Every entity has one. An asset with no owning agency cannot be tasked,
 * and an untaskable asset does not belong in an operational twin. */
export const AGENCIES = {
  POLICE:    { id: "POLICE",    label: { en: "Police",             mr: "पोलीस" } },
  HEALTH:    { id: "HEALTH",    label: { en: "Health",             mr: "आरोग्य" } },
  TRANSPORT: { id: "TRANSPORT", label: { en: "Transport",          mr: "परिवहन" } },
  NMC:       { id: "NMC",       label: { en: "Municipal (NMC)",    mr: "महानगरपालिका" } },
  REVENUE:   { id: "REVENUE",   label: { en: "Revenue",            mr: "महसूल" } },
  NDRF:      { id: "NDRF",      label: { en: "NDRF",               mr: "एनडीआरएफ" } },
  FIRE:      { id: "FIRE",      label: { en: "Fire",               mr: "अग्निशमन" } },
  COMMS:     { id: "COMMS",     label: { en: "Communications",     mr: "जनसंपर्क" } },
};

/* ── Entity types ──────────────────────────────────────────────────────
 * The list is the brief's, in the brief's order, so coverage can be checked
 * against it line by line. `kind` groups them for the twin's own logic. */
export const ENTITY_TYPES = {
  ghat:           { kind: "place",     agency: AGENCIES.NMC,       label: { en: "Ghat",             mr: "घाट" } },
  road:           { kind: "link",      agency: AGENCIES.NMC,       label: { en: "Road",             mr: "रस्ता" } },
  bridge:         { kind: "link",      agency: AGENCIES.NMC,       label: { en: "Bridge",           mr: "पूल" } },
  junction:       { kind: "node",      agency: AGENCIES.POLICE,    label: { en: "Junction",         mr: "चौक" } },
  gate:           { kind: "node",      agency: AGENCIES.POLICE,    label: { en: "Gate",             mr: "प्रवेशद्वार" } },
  barricade:      { kind: "control",   agency: AGENCIES.POLICE,    label: { en: "Barricade",        mr: "बॅरिकेड" } },
  corridor:       { kind: "link",      agency: AGENCIES.POLICE,    label: { en: "Corridor",         mr: "मार्गिका" } },
  parking:        { kind: "place",     agency: AGENCIES.TRANSPORT, label: { en: "Parking area",     mr: "वाहनतळ" } },
  camp:           { kind: "place",     agency: AGENCIES.REVENUE,   label: { en: "Camp",             mr: "तळ" } },
  toiletCluster:  { kind: "service",   agency: AGENCIES.NMC,       label: { en: "Toilet cluster",   mr: "स्वच्छतागृह संकुल" } },
  waterStation:   { kind: "service",   agency: AGENCIES.NMC,       label: { en: "Water station",    mr: "जल केंद्र" } },
  hospital:       { kind: "service",   agency: AGENCIES.HEALTH,    label: { en: "Hospital",         mr: "रुग्णालय" } },
  medicalCamp:    { kind: "service",   agency: AGENCIES.HEALTH,    label: { en: "Medical camp",     mr: "वैद्यकीय शिबिर" } },
  ambulance:      { kind: "unit",      agency: AGENCIES.HEALTH,    label: { en: "Ambulance",        mr: "रुग्णवाहिका" } },
  policePost:     { kind: "service",   agency: AGENCIES.POLICE,    label: { en: "Police post",      mr: "पोलीस चौकी" } },
  fireStation:    { kind: "service",   agency: AGENCIES.FIRE,      label: { en: "Fire station",     mr: "अग्निशमन केंद्र" } },
  railwayStation: { kind: "place",     agency: AGENCIES.TRANSPORT, label: { en: "Railway station",  mr: "रेल्वे स्थानक" } },
  busTerminal:    { kind: "place",     agency: AGENCIES.TRANSPORT, label: { en: "Bus terminal",     mr: "बस स्थानक" } },
  emergencyRoute: { kind: "link",      agency: AGENCIES.NDRF,      label: { en: "Emergency route",  mr: "आपत्कालीन मार्ग" } },
  camera:         { kind: "sensor",    agency: AGENCIES.POLICE,    label: { en: "CCTV camera",      mr: "सीसीटीव्ही कॅमेरा" } },
  paSystem:       { kind: "actuator",  agency: AGENCIES.COMMS,     label: { en: "PA system",        mr: "ध्वनिक्षेपक" } },
  vms:            { kind: "actuator",  agency: AGENCIES.COMMS,     label: { en: "VMS / signage",    mr: "डिजिटल फलक" } },
  sector:         { kind: "area",      agency: AGENCIES.REVENUE,   label: { en: "Sector",           mr: "सेक्टर" } },
  officer:        { kind: "unit",      agency: AGENCIES.POLICE,    label: { en: "Officer / resource", mr: "अधिकारी / संसाधन" } },
};

/* ── Relationship verbs ────────────────────────────────────────────────
 * Typed, directed, and few. A graph with thirty edge types is a graph
 * nobody traverses correctly. */
export const REL = {
  FEEDS:      "feeds",       // upstream → downstream in the movement chain
  EXITS_TO:   "exits_to",    // place → the link that drains it
  CONSTRAINS: "constrains",  // a control that limits a link's capacity
  SERVES:     "serves",      // a service/unit → the area it covers
  WITHIN:     "within",      // anything → its sector
  WATCHES:    "watches",     // a sensor → what it observes
  ADDRESSES:  "addresses",   // an actuator → the area it can reach
};

/* ── Entities invented to complete the twin ────────────────────────────
 * Everything the brief lists that the existing geo data did not have. */

const E = (id, type, name, coord, sector, extra = {}) =>
  withProvenance(
    { id, type, name, coord, sector, ...extra },
    demo(`${ENTITY_TYPES[type].label.en} invented for demo. Replace with the district asset register.`),
  );

export const BRIDGES = [
  E("BRG-01", "bridge", { en: "Ahilyabai Holkar Bridge", mr: "अहिल्याबाई होळकर पूल" },
    [73.7908, 19.9996], "SEC-01", { widthM: 12, capacityPerMin: 936, state: "open" }),
  E("BRG-02", "bridge", { en: "Panchavati footbridge", mr: "पंचवटी पादचारी पूल" },
    [73.7955, 20.0062], "SEC-02", { widthM: 6, capacityPerMin: 468, state: "open" }),
];

export const JUNCTIONS = [
  E("JN-01", "junction", { en: "Ramkund Chowk", mr: "रामकुंड चौक" },
    [73.7893, 19.9982], "SEC-01", { approaches: 4, signalised: true }),
  E("JN-02", "junction", { en: "Panchavati Karanja", mr: "पंचवटी कारंजा" },
    [73.7968, 20.0088], "SEC-02", { approaches: 5, signalised: true }),
  E("JN-03", "junction", { en: "Trimbak Naka", mr: "त्र्यंबक नाका" },
    [73.5322, 19.9412], "SEC-03", { approaches: 3, signalised: false }),
];

/* Barricades are the twin's control surface — the things an officer can
 * actually change in the field, which is why §6 scenario F depends on them
 * existing as entities rather than as a line on a plan. */
export const BARRICADES = [
  E("BAR-01", "barricade", { en: "Ramkund inflow barricade", mr: "रामकुंड प्रवेश बॅरिकेड" },
    [73.7885, 19.9978], "SEC-01",
    { constrains: "COR-01", configuration: "single-file", throughputPerMin: 320, adjustable: true }),
  E("BAR-02", "barricade", { en: "Ramkund holding pen", mr: "रामकुंड प्रतीक्षा कक्ष" },
    [73.7872, 19.9968], "SEC-01",
    { constrains: "COR-01", configuration: "holding", throughputPerMin: 0, holdingCapacity: 4000, adjustable: true }),
  E("BAR-03", "barricade", { en: "Panchavati lane divider", mr: "पंचवटी मार्गिका विभाजक" },
    [73.7960, 20.0072], "SEC-02",
    { constrains: "COR-02", configuration: "two-way-split", throughputPerMin: 620, adjustable: true }),
  E("BAR-04", "barricade", { en: "Kushavarta approach barricade", mr: "कुशावर्त बॅरिकेड" },
    [73.5300, 19.9396], "SEC-03",
    { constrains: "COR-03", configuration: "single-file", throughputPerMin: 280, adjustable: true }),
];

/* Corridors are the pedestrian links the crowd actually moves along. Their
 * capacity is derived from width by the same pedestrian-flow figure the
 * live model uses for egress, so the two cannot drift apart. */
const CORRIDOR = (id, name, from, to, sector, widthM, lengthM) =>
  withProvenance(
    {
      id, type: "corridor", name, sector, widthM, lengthM,
      from, to,
      capacityPerMin: Math.round(widthM * 1.3 * 60),
      coord: null,
    },
    demo("Corridor alignment and width invented for demo."),
  );

export const CORRIDORS = [
  CORRIDOR("COR-01", { en: "Ramkund approach corridor", mr: "रामकुंड मार्गिका" },
    "JN-01", "GHAT-01", "SEC-01", 10, 420),
  CORRIDOR("COR-02", { en: "Panchavati link corridor", mr: "पंचवटी जोड मार्गिका" },
    "JN-02", "GHAT-05", "SEC-02", 8, 600),
  CORRIDOR("COR-03", { en: "Kushavarta approach", mr: "कुशावर्त मार्गिका" },
    "JN-03", "GHAT-04", "SEC-03", 7, 350),
  CORRIDOR("COR-04", { en: "Tapovan camp walk", mr: "तपोवन पदपथ" },
    "PARK-01", "GHAT-03", "SEC-04", 9, 800),
  CORRIDOR("COR-05", { en: "Station transfer walk", mr: "स्थानक पदपथ" },
    "TRN-01", "BUSTERM-01", "SEC-05", 12, 300),
];

export const BUS_TERMINALS = [
  E("BUSTERM-01", "busTerminal", { en: "Nashik Road bus terminal", mr: "नाशिक रोड बस स्थानक" },
    [73.8372, 19.9471], "SEC-05", { bays: 24, dispatchPerHour: 180 }),
  E("BUSTERM-02", "busTerminal", { en: "Gangapur shuttle terminal", mr: "गंगापूर शटल स्थानक" },
    [73.6852, 20.0162], "SEC-06", { bays: 12, dispatchPerHour: 90 }),
];

export const CAMPS = [
  E("CAMP-01", "camp", { en: "Akhara camp — Tapovan", mr: "आखाडा तळ — तपोवन" },
    [73.8112, 20.0210], "SEC-04", { capacity: 18000, occupancy: 11200 }),
  E("CAMP-02", "camp", { en: "Pilgrim rest camp — Gangapur", mr: "यात्री विश्राम तळ — गंगापूर" },
    [73.6880, 20.0150], "SEC-06", { capacity: 9000, occupancy: 3100 }),
];

/* Cameras exist as entities so §16 can report feed health per sector, and so
 * the twin can say which parts of the theatre are unobserved. The POC has no
 * video pipeline — `connected: false` is the honest state and the assurance
 * layer reports it as such rather than showing a green tick. */
const CAM = (id, name, coord, sector, watches) =>
  withProvenance(
    { id, type: "camera", name, coord, sector, watches, connected: false },
    demo("Camera position invented. No video pipeline is attached to this POC.",
         CLASSIFICATION.RESTRICTED),
  );

export const CAMERAS = [
  CAM("CAM-01", { en: "Ramkund ghat overview", mr: "रामकुंड घाट" },      [73.7896, 19.9986], "SEC-01", "GHAT-01"),
  CAM("CAM-02", { en: "Ramkund corridor", mr: "रामकुंड मार्गिका" },       [73.7889, 19.9980], "SEC-01", "COR-01"),
  CAM("CAM-03", { en: "Panchavati approach", mr: "पंचवटी मार्ग" },        [73.7962, 20.0080], "SEC-02", "COR-02"),
  CAM("CAM-04", { en: "Trimbak Kushavarta", mr: "त्र्यंबक कुशावर्त" },     [73.5302, 19.9398], "SEC-03", "GHAT-04"),
  CAM("CAM-05", { en: "Station concourse", mr: "स्थानक प्रांगण" },        [73.8388, 19.9480], "SEC-05", "TRN-01"),
];

/* Actuators — the things a decision can actually operate. §6 scenario H is
 * only meaningful if the platform knows which PA arrays reach which sector. */
const ACTUATOR = (id, type, name, coord, sector, addresses, extra = {}) =>
  withProvenance(
    { id, type, name, coord, sector, addresses, ...extra },
    demo("Actuator placement invented for demo."),
  );

export const PA_SYSTEMS = [
  ACTUATOR("PA-01", "paSystem", { en: "Ramkund PA array", mr: "रामकुंड ध्वनिक्षेपक" },
    [73.7894, 19.9984], "SEC-01", ["SEC-01"], { languages: ["mr", "hi", "en"], zones: 6 }),
  ACTUATOR("PA-02", "paSystem", { en: "Panchavati PA array", mr: "पंचवटी ध्वनिक्षेपक" },
    [73.7966, 20.0085], "SEC-02", ["SEC-02"], { languages: ["mr", "hi", "en"], zones: 4 }),
  ACTUATOR("PA-03", "paSystem", { en: "Trimbak PA array", mr: "त्र्यंबक ध्वनिक्षेपक" },
    [73.5312, 19.9405], "SEC-03", ["SEC-03"], { languages: ["mr", "hi", "en"], zones: 3 }),
  ACTUATOR("PA-04", "paSystem", { en: "Station PA", mr: "स्थानक ध्वनिक्षेपक" },
    [73.8390, 19.9483], "SEC-05", ["SEC-05"], { languages: ["mr", "hi", "en", "gu"], zones: 2 }),
];

export const VMS_BOARDS = [
  ACTUATOR("VMS-01", "vms", { en: "VMS — Ramkund approach", mr: "फलक — रामकुंड" },
    [73.7880, 19.9962], "SEC-01", ["SEC-01"], { lines: 3, languages: ["mr", "hi", "en"] }),
  ACTUATOR("VMS-02", "vms", { en: "VMS — Trimbak highway", mr: "फलक — त्र्यंबक महामार्ग" },
    [73.6400, 19.9550], "SEC-03", ["SEC-03"], { lines: 3, languages: ["mr", "hi", "en"] }),
  ACTUATOR("VMS-03", "vms", { en: "VMS — Nashik Road", mr: "फलक — नाशिक रोड" },
    [73.8340, 19.9500], "SEC-05", ["SEC-05"], { lines: 2, languages: ["mr", "hi", "en"] }),
];

/* Deployable units. §12 selects from these, which is only defensible if each
 * one has a real position and a state that can make it unavailable. */
const UNIT = (id, type, name, coord, sector, extra = {}) =>
  withProvenance(
    { id, type, name, coord, sector, status: "available", ...extra },
    demo("Unit deployment invented for demo.", CLASSIFICATION.RESTRICTED),
  );

export const AMBULANCES = [
  UNIT("AMBU-01", "ambulance", { en: "Ambulance 108-A", mr: "रुग्णवाहिका 108-अ" }, [73.7880, 19.9992], "SEC-01", { advancedLifeSupport: true,  baseId: "AMB-01" }),
  UNIT("AMBU-02", "ambulance", { en: "Ambulance 108-B", mr: "रुग्णवाहिका 108-ब" }, [73.7882, 19.9990], "SEC-01", { advancedLifeSupport: false, baseId: "AMB-01" }),
  UNIT("AMBU-03", "ambulance", { en: "Ambulance 108-C", mr: "रुग्णवाहिका 108-क" }, [73.5288, 19.9398], "SEC-03", { advancedLifeSupport: true,  baseId: "AMB-02" }),
  UNIT("AMBU-04", "ambulance", { en: "Ambulance 108-D", mr: "रुग्णवाहिका 108-ड" }, [73.8112, 20.0200], "SEC-04", { advancedLifeSupport: false, baseId: "AMB-02" }),
];

export const OFFICER_UNITS = [
  UNIT("UNIT-P1", "officer", { en: "QRT Alpha (police)", mr: "क्यूआरटी अल्फा" },      [73.7889, 19.9998], "SEC-01", { strength: 12, agency: "POLICE" }),
  UNIT("UNIT-P2", "officer", { en: "QRT Bravo (police)", mr: "क्यूआरटी ब्राव्हो" },   [73.7975, 20.0105], "SEC-02", { strength: 10, agency: "POLICE" }),
  UNIT("UNIT-P3", "officer", { en: "QRT Charlie (police)", mr: "क्यूआरटी चार्ली" },   [73.5305, 19.9400], "SEC-03", { strength: 8,  agency: "POLICE" }),
  UNIT("UNIT-N1", "officer", { en: "NDRF Team 1", mr: "एनडीआरएफ पथक 1" },            [73.7905, 19.9945], "SEC-01", { strength: 15, agency: "NDRF" }),
  UNIT("UNIT-F1", "officer", { en: "Fire tender 1", mr: "अग्निशमन वाहन 1" },          [73.7998, 20.0088], "SEC-02", { strength: 6,  agency: "FIRE" }),
];

/* Parking bays. The mobility chain derives private-vehicle ingress
 * capacity from these rather than from a flat per-lot guess, so the figure
 * is traceable to an asset a department could count. */
export const PARKING_BAYS = {
  "PARK-01": 2400,
  "PARK-02": 3200,
};
export const VEHICLE_OCCUPANCY = 3.5;
export const PARKING_DWELL_HOURS = 5;

/* Hospital capacity — §12 needs somewhere to take a casualty, and "nearest
 * hospital" is the wrong answer if it has no beds. */
export const HOSPITAL_CAPACITY = {
  "HOSP-01": { beds: 420, occupied: 348, emergencyBays: 12, emergencyOccupied: 7, trauma: true },
  "HOSP-02": { beds: 90,  occupied: 61,  emergencyBays: 4,  emergencyOccupied: 3, trauma: false },
  "MED-01":  { beds: 24,  occupied: 11,  emergencyBays: 3,  emergencyOccupied: 1, trauma: false },
  "MED-02":  { beds: 18,  occupied: 4,   emergencyBays: 2,  emergencyOccupied: 0, trauma: false },
};

/* Re-export the pre-existing collections so consumers have one import. */
export { SECTORS, GHATS, ENTRY_GATES, EXIT_GATES, FACILITIES, ROUTES, CONGREGATION };
